import { useState } from 'react';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/base/Breadcrumb';

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫屋',
    description: '韩漫屋-好看的韩国漫画免费阅读平台，了解品牌故事、平台优势与商务合作',
    url: 'http://nheap.cn/about',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫屋',
    url: 'http://nheap.cn/',
    description: '韩漫屋平台汇集了好看的韩国漫画，支持韩漫免费阅读，提供专业贴心的漫画服务',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'hello@nheap.cn',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://nheap.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  },
];

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypotVal = (fd.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      setFormMsg('提交成功！我们会尽快与您联系。');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }
    if (formData.message.length > 500) {
      setFormStatus('error');
      setFormMsg('留言内容不能超过500个字符。');
      return;
    }
    setFormStatus('loading');
    try {
      setFormStatus('success');
      setFormMsg('提交成功！我们会尽快与您联系。');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 韩漫屋 - 好看的韩国漫画免费阅读平台"
        description="了解韩漫屋的品牌故事、平台优势与服务理念。韩漫屋-好看的韩国漫画免费阅读平台，通过韩漫网址快速访问，享受专业贴心的漫画阅读服务。商务合作请联系我们。"
        keywords="好看的韩国漫画, 韩漫免费阅读, 韩漫网址, 韩漫屋, 关于我们"
        canonicalUrl="http://nheap.cn/about"
        jsonLd={aboutJsonLd}
      />

      <main className="pt-16 min-h-screen">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-10 md:py-14">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-900 mb-4">
                关于韩漫屋
              </h1>
              <p className="text-foreground-600 leading-relaxed">
                韩漫屋是一个专注于韩国漫画的免费阅读平台，致力于为漫画爱好者提供优质的在线阅读体验。
                我们汇集了恋爱、奇幻、动作、悬疑等多种类型的韩漫作品，每日更新热门漫画。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-14">
              {[
                { icon: 'ri-book-open-line', num: '10,000+', label: '漫画作品', desc: '涵盖多种类型的韩漫资源' },
                { icon: 'ri-user-heart-line', num: '500万+', label: '活跃读者', desc: '深受漫画爱好者信赖' },
                { icon: 'ri-timer-line', num: '每日更新', label: '稳定连载', desc: '持续更新热门漫画作品' },
                { icon: 'ri-shield-check-line', num: '免费阅读', label: '零门槛', desc: '所有漫画免费在线观看' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100">
                  <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mx-auto mb-3">
                    <i className={`${stat.icon} text-2xl`}></i>
                  </span>
                  <p className="font-heading font-bold text-2xl text-foreground-900">{stat.num}</p>
                  <p className="font-medium text-foreground-800 mt-1">{stat.label}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.desc}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
              <div>
                <h2 className="font-heading font-bold text-2xl text-foreground-900 mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩漫屋诞生于对韩国漫画的热爱。我们的创始团队是一群资深的漫画爱好者，深知读者对于高质量漫画阅读体验的渴望。
                    在数字阅读时代，我们看到了韩国漫画市场的巨大潜力，于是创建了韩漫屋这个平台。
                  </p>
                  <p>
                    作为<strong>好看的韩国漫画</strong>免费阅读平台，韩漫屋始终坚持内容为王的原则。我们与多家韩国漫画工作室建立了合作关系，
                    确保平台上的每一部作品都具有高品质的画风和引人入胜的故事情节。
                  </p>
                  <p>
                    通过<strong>韩漫网址</strong> nheap.cn，读者可以随时随地访问韩漫屋，<strong>韩漫免费阅读</strong>不再受时间和空间的限制。
                    无论是通勤路上还是睡前时光，打开韩漫屋，就能沉浸在最精彩的漫画世界中。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading font-bold text-2xl text-foreground-900 mb-4">平台优势</h2>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-smartphone-line', title: '全设备支持', desc: '支持PC端和移动端访问，随时随地畅读韩漫' },
                    { icon: 'ri-image-line', title: '高清画质', desc: '所有漫画采用高清扫描，确保最佳阅读体验' },
                    { icon: 'ri-speed-up-line', title: '极速加载', desc: '优化的图片加载技术，翻页流畅不卡顿' },
                    { icon: 'ri-notification-3-line', title: '更新提醒', desc: '订阅后第一时间收到漫画更新通知' },
                    { icon: 'ri-group-line', title: '读者社区', desc: '与千万漫画爱好者交流讨论，分享阅读心得' },
                    { icon: 'ri-lock-unlock-line', title: '完全免费', desc: '无需付费订阅，所有内容免费阅读' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3 p-4 rounded-xl bg-background-100">
                      <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className={`${item.icon} text-lg`}></i>
                      </span>
                      <div>
                        <h3 className="font-medium text-foreground-900 text-sm">{item.title}</h3>
                        <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <section className="bg-background-100 rounded-2xl p-6 md:p-10">
              <div className="max-w-2xl mx-auto">
                <h2 className="font-heading font-bold text-2xl text-foreground-900 text-center mb-2">商务合作</h2>
                <p className="text-sm text-foreground-500 text-center mb-8">
                  如果您有漫画资源合作、广告投放或其他商务需求，请通过以下表单联系我们
                </p>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="form-shield">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题 *</label>
                    <input
                      id="contact-subject"
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData((prev) => ({ ...prev, subject: e.target.value }))}
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入合作主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容 *</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请描述您的合作需求（最多500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer disabled:opacity-60"
                  >
                    {formStatus === 'loading' ? '提交中...' : '提交合作需求'}
                  </button>
                  {formStatus !== 'idle' && (
                    <p className={`text-sm text-center ${formStatus === 'success' ? 'text-primary-600' : 'text-accent-600'}`}>
                      {formMsg}
                    </p>
                  )}
                </form>

                <div className="mt-8 pt-6 border-t border-background-200 flex flex-col sm:flex-row items-center justify-center gap-6">
                  <div className="flex items-center gap-2 text-sm text-foreground-500">
                    <i className="ri-mail-line text-lg text-primary-500"></i>
                    <span>hello@nheap.cn</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground-500">
                    <i className="ri-customer-service-2-line text-lg text-primary-500"></i>
                    <span>工作日 9:00 - 18:00</span>
                  </div>
                </div>
              </div>
            </section>
          </section>
        </div>
      </main>
    </>
  );
}
